import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CategoryFormView } from '@today/ui-domain'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { productCapabilities } from '@/auth/product-capabilities'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { type CategoryDTO, productService } from '@/services/product.service'
import { categoryErrors, EMPTY_CATEGORY_FORM, toCategoryBody } from './category-form'
import { productFieldError, translateKeys } from './product-ui'

interface Editor {
	id: string | null
	values: CategoryFormView
	errors: Record<string, string>
	formError: string | null
}

const fromCategory = (c: CategoryDTO): CategoryFormView => ({
	code: c.code,
	name: c.name,
	icon: c.icon ?? '',
	sortOrder: String(c.sortOrder),
})

/** The dialog after a server refusal: on its field, or at the top when no field relates. */
const editorWithError = (target: Editor, { field, message }: { field: string; message: string }): Editor =>
	field === 'form'
		? { ...target, errors: {}, formError: message }
		: { ...target, errors: { [field]: message }, formError: null }

/** Create/update and delete; both reload the list on success. */
function useCategoryMutations(handlers: {
	onSaved: () => void
	onSaveError: (error: unknown, target: Editor) => void
	onDeleted: () => void
	onDeleteError: (error: unknown) => void
}) {
	const client = useQueryClient()
	const refresh = () => client.invalidateQueries({ queryKey: ['product-categories'] })
	const save = useMutation({
		mutationFn: (target: Editor) =>
			target.id
				? productService.updateCategory(target.id, toCategoryBody(target.values))
				: productService.createCategory(toCategoryBody(target.values)),
		onSuccess: async () => {
			handlers.onSaved()
			await refresh()
		},
		onError: handlers.onSaveError,
	})
	const remove = useMutation({
		mutationFn: (id: string) => productService.deleteCategory(id),
		onSuccess: async () => {
			handlers.onDeleted()
			await refresh()
		},
		onError: handlers.onDeleteError,
	})
	return { save, remove }
}

/** Category management (TO-416 product-admin-ui REQ-008): ordered list, dialog create/edit, guarded delete. */
export function useProductCategories() {
	const { t, i18n } = useTranslation('products')
	const caps = productCapabilities(useViewerPermissions().permissions)
	const list = useQuery({ queryKey: ['product-categories'], queryFn: productService.allCategories })
	const rows = list.data ?? []
	const [editor, setEditor] = useState<Editor | null>(null)
	const [deleting, setDeleting] = useState<{ id: string; name: string; error: string | null } | null>(null)
	const fieldError = (e: unknown) => productFieldError(e, t, (key) => i18n.exists(key, { ns: 'products' }), 'category')
	const { save, remove } = useCategoryMutations({
		onSaved: () => setEditor(null),
		onSaveError: (e, target) => setEditor(editorWithError(target, fieldError(e))),
		onDeleted: () => setDeleting(null),
		onDeleteError: (e) => setDeleting((d) => (d ? { ...d, error: fieldError(e).message } : d)),
	})
	const find = (id: string) => rows.find((c) => c.categoryId === id)

	return {
		t,
		rows: rows.map((c) => ({ id: c.categoryId, code: c.code, name: c.name, icon: c.icon, sortOrder: c.sortOrder })),
		error: list.isError,
		editor: editor ? { mode: editor.id ? ('edit' as const) : ('create' as const), ...editor } : null,
		deleting,
		isBusy: save.isPending || remove.isPending,
		onRetry: () => void list.refetch(),
		onEditorChange: (field: keyof CategoryFormView, value: string) =>
			setEditor((e) => (e ? { ...e, values: { ...e.values, [field]: value } } : e)),
		onEditorSubmit: () => {
			if (!editor) return
			const found = categoryErrors(editor.values)
			setEditor({ ...editor, errors: translateKeys(found, t), formError: null })
			if (Object.keys(found).length === 0) save.mutate(editor)
		},
		onEditorClose: () => setEditor(null),
		onConfirmDelete: () => (deleting ? remove.mutate(deleting.id) : undefined),
		onCancelDelete: () => setDeleting(null),
		onNew: caps.write
			? () => setEditor({ id: null, values: EMPTY_CATEGORY_FORM, errors: {}, formError: null })
			: undefined,
		onEdit: caps.write ? (id: string) => openEdit(find(id)) : undefined,
		onDelete: caps.delete ? (id: string) => openDelete(find(id)) : undefined,
	}

	function openEdit(found: CategoryDTO | undefined) {
		if (found) setEditor({ id: found.categoryId, values: fromCategory(found), errors: {}, formError: null })
	}
	function openDelete(found: CategoryDTO | undefined) {
		if (found) setDeleting({ id: found.categoryId, name: found.name, error: null })
	}
}
